import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import ReactGridLayout, { Layout, LayoutItem, noCompactor } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  GridLayoutConfig,
  GridItemPosition,
  getTabGroup,
  DEFAULT_COLS,
  DEFAULT_ROWS,
} from '../../types/gridLayout';
import { DashboardWidgetInstance } from '../../types';
import GridItemWrapper from './GridItemWrapper';
import TabGroupContainer from './TabGroupContainer';

interface DashboardGridProps {
  layoutConfig: GridLayoutConfig;
  widgets: DashboardWidgetInstance[];
  isEditable: boolean;
  onLayoutChange?: (newConfig: GridLayoutConfig) => void;
  renderWidget: (widgetId: string) => React.ReactNode;
  onActiveTabChange?: (tabGroupId: string, widgetId: string) => void;
  // Widget metadata for display
  getWidgetTitle?: (widgetId: string) => string;
  getWidgetIsInput?: (widgetId: string) => boolean;
  getWidgetRefresh?: (widgetId: string) => (() => void) | undefined;
  getWidgetIsSummary?: (widgetId: string) => boolean;
  getWidgetIsShared?: (widgetId: string) => boolean;
  hideMaximize?: boolean;
  /** IDs of widgets that overlap with another widget — shown with a red outline */
  overlappingItemIds?: Set<string>;
}

const DashboardGrid: React.FC<DashboardGridProps> = ({
  layoutConfig,
  widgets,
  isEditable,
  onLayoutChange,
  renderWidget,
  onActiveTabChange,
  getWidgetTitle,
  getWidgetIsInput,
  getWidgetRefresh,
  getWidgetIsSummary,
  getWidgetIsShared,
  hideMaximize,
  overlappingItemIds,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  // Measure container width and height
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const cols = layoutConfig.cols || DEFAULT_COLS;
  const authoredRows = layoutConfig.rows || DEFAULT_ROWS;
  // In read-only (worker) view, collapse trailing empty rows: shrink the grid to
  // the last occupied row so sparse steps fill the pane instead of leaving large
  // dead space below. Never exceed the authored row count (edit mode is untouched).
  const occupiedRows = layoutConfig.layout.reduce((m, it) => Math.max(m, it.y + it.h), 0);
  const rows = isEditable ? authoredRows : Math.min(authoredRows, Math.max(occupiedRows, 1));
  const margin = 8;
  const padding = 8;

  // Compute rowHeight from container height, with a minimum to prevent squishing
  const MIN_ROW_HEIGHT = 36;
  const computedRowHeight = containerHeight > 0
    ? (containerHeight - padding * 2 - (rows - 1) * margin) / rows
    : 0;
  const rowHeight = Math.max(computedRowHeight, MIN_ROW_HEIGHT);

  // If the grid needs more space than the container, allow scrolling
  const needsScroll = rowHeight > computedRowHeight;

  // Grid lines showing the 12-column layout in edit mode
  // Matches react-grid-layout's position formula:
  //   colWidth = (containerWidth - 2*padding - (cols-1)*margin) / cols
  //   cellX = padding + col * (colWidth + margin)
  const gridBgStyle = useMemo<React.CSSProperties>(() => {
    if (!isEditable || containerWidth <= 0 || rowHeight <= 0) return {};
    const colWidth = (containerWidth - padding * 2 - (cols - 1) * margin) / cols;
    const cellWidth = colWidth + margin;
    const cellHeight = rowHeight + margin;
    const dot = 'var(--color-neutral-400)';
    return {
      backgroundImage:
        // Dot at the center where 4 cells meet (middle of margin cross)
        `radial-gradient(circle at ${colWidth + margin / 2}px ${rowHeight + margin / 2}px, ${dot} 2px, transparent 2px)`,
      backgroundSize: `${cellWidth}px ${cellHeight}px`,
      backgroundPosition: `${padding}px ${padding}px`,
    };
  }, [isEditable, containerWidth, rowHeight, cols, margin, padding]);

  // Convert layout to react-grid-layout format, applying static flag for workers
  const rglLayout: LayoutItem[] = useMemo(() => {
    return layoutConfig.layout.map((item) => ({
      ...item,
      static: !isEditable ? true : (item.static || false),
    }));
  }, [layoutConfig.layout, isEditable]);

  // Handle layout change from react-grid-layout
  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      if (!onLayoutChange || !isEditable) return;

      // Merge new positions back into config, preserving our extra fields
      // Clamp positions so widgets stay within the grid bounds
      const updatedLayout: GridItemPosition[] = newLayout.map((item) => {
        const existing = layoutConfig.layout.find((l) => l.i === item.i);
        const clampedX = Math.max(0, Math.min(item.x, cols - item.w));
        const clampedY = Math.max(0, Math.min(item.y, rows - item.h));
        return {
          i: item.i,
          x: clampedX,
          y: clampedY,
          w: item.w,
          h: item.h,
          minW: existing?.minW,
          minH: existing?.minH,
          maxW: existing?.maxW,
          maxH: existing?.maxH,
        };
      });

      onLayoutChange({
        ...layoutConfig,
        layout: updatedLayout,
      });
    },
    [onLayoutChange, isEditable, layoutConfig, cols, rows]
  );

  // Render a single grid item's content
  const renderGridItem = useCallback(
    (itemId: string) => {
      const tabGroup = getTabGroup(itemId, layoutConfig);

      if (tabGroup) {
        // Render as tab group
        const tabs = tabGroup.widgetIds.map((wId) => ({
          widgetId: wId,
          title: getWidgetTitle?.(wId) || 'Widget',
          isInput: getWidgetIsInput?.(wId),
        }));

        return (
          <TabGroupContainer
            tabs={tabs}
            activeWidgetId={tabGroup.activeWidgetId}
            isEditable={isEditable}
            hideMaximize={hideMaximize}
            renderWidget={renderWidget}
            onActiveTabChange={(widgetId) => onActiveTabChange?.(itemId, widgetId)}
          />
        );
      }

      // Render as standalone widget
      return (
        <GridItemWrapper
          title={getWidgetTitle?.(itemId) || 'Widget'}
          isEditable={isEditable}
          isInput={getWidgetIsInput?.(itemId)}
          isSummary={getWidgetIsSummary?.(itemId)}
          isShared={getWidgetIsShared?.(itemId)}
          hideMaximize={hideMaximize}
          onRefresh={getWidgetRefresh?.(itemId)}
        >
          {renderWidget(itemId)}
        </GridItemWrapper>
      );
    },
    [
      layoutConfig,
      isEditable,
      renderWidget,
      getWidgetTitle,
      getWidgetIsInput,
      getWidgetRefresh,
      getWidgetIsSummary,
      getWidgetIsShared,
      hideMaximize,
      onActiveTabChange,
    ]
  );

  return (
    <div ref={containerRef} className={`dashboard-grid h-full w-full ${needsScroll ? 'overflow-auto' : 'overflow-hidden'}`} style={gridBgStyle}>
      {containerWidth > 0 && rowHeight > 0 && (
        <ReactGridLayout
          className={`react-grid-layout ${isEditable ? 'react-grid-layout--editable' : ''}`}
          layout={rglLayout}
          width={containerWidth}
          gridConfig={{
            cols,
            rowHeight,
            maxRows: rows,
            margin: [8, 8] as readonly [number, number],
            containerPadding: [8, 8] as readonly [number, number],
          }}
          dragConfig={{
            enabled: isEditable,
            handle: '.grid-item-titlebar--editable, .tab-group-tabs',
          }}
          resizeConfig={{
            enabled: isEditable,
          }}
          compactor={{ ...noCompactor, allowOverlap: true }}
          onLayoutChange={handleLayoutChange}
        >
          {rglLayout.map((item) => (
            <div
              key={item.i}
              className={`grid-item-outer ${overlappingItemIds?.has(item.i) ? 'ring-2 ring-danger-500 rounded-lg z-50' : ''}`}
            >
              {renderGridItem(item.i)}
            </div>
          ))}
        </ReactGridLayout>
      )}
    </div>
  );
};

export default React.memo(DashboardGrid);
